import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth, isStaff, isStudent, isTeacher, isAdminOrSegreteria, isFundraising, isEconomato } from "@/lib/auth";
import { canAccessPath } from "@/lib/permissions";
import { Toaster } from "@/components/ui/sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Menu, Sprout } from "lucide-react";
import PushEnableButton from "@/components/PushEnableButton";
import PullToRefresh from "./PullToRefresh";

export default function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // chiudi il drawer quando cambia la rotta
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (user === undefined) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
        <div className="text-stone-500 text-sm">Caricamento…</div>
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;

  const path = location.pathname;
  const staff = isStaff(user);
  const student = isStudent(user);
  const teacher = isTeacher(user);
  const adminSegreteria = isAdminOrSegreteria(user);

  // studente non può accedere alle aree staff, e viceversa
  if (student && !(path === "/portale" || path.startsWith("/portale") || path === "/helpdesk" || path.startsWith("/helpdesk/"))) {
    return <Navigate to="/portale" replace />;
  }
  if (staff && path.startsWith("/portale")) {
    return <Navigate to="/" replace />;
  }
  const crmOnlyPaths = ["/donatori", "/donazioni", "/scadenze", "/followup", "/comunicazioni", "/report", "/impostazioni", "/mansioni"];
  const isCrmPath = crmOnlyPaths.some((p) => path === p || path.startsWith(`${p}/`));
  if (teacher && !adminSegreteria && (path === "/" || isCrmPath)) {
    return <Navigate to="/docente" replace />;
  }
  // Docente: solo le sezioni del proprio menu
  const TEACHER_PATHS = ["/docente", "/scuola/piano", "/scuola/corsi", "/scuola/statistiche", "/scuola/esami", "/scuola/avvisi", "/richieste", "/messaggi", "/helpdesk"];
  const teacherAllowed = TEACHER_PATHS.some((p) => path === p || path.startsWith(`${p}/`)) || /^\/scuola\/studenti\/[^/]+\/statistiche$/.test(path);
  if (teacher && !teacherAllowed) {
    return <Navigate to="/docente" replace />;
  }

  // Ruolo economato: solo Wallet
  if (isEconomato(user) && !(path === "/wallet" || path.startsWith("/wallet/"))) {
    return <Navigate to="/wallet" replace />;
  }

  // Ruolo raccolta_fondi: solo dashboard + moduli raccolta fondi
  if (isFundraising(user)) {
    const fundraisingPaths = ["/donatori", "/donazioni", "/scadenze", "/followup", "/comunicazioni", "/report"];
    const isFundraisingPath = path === "/" || fundraisingPaths.some((p) => path === p || path.startsWith(`${p}/`));
    if (!isFundraisingPath) return <Navigate to="/" replace />;
  }

  // Enforcement dei permessi granulari (solo ruoli segreteria / raccolta fondi / economato)
  if (
    ["segreteria", "raccolta_fondi", "economato"].includes(user.role) &&
    path !== "/checkin" &&
    !canAccessPath(user, path)
  ) {
    return <Navigate to="/" replace />;
  }

  const mustChange = !!user?.must_change_password;

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] grain-bg">
      {/* Sidebar desktop: nascosta sotto lg */}
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col lg:pl-72">
        {/* Top bar mobile: visibile solo sotto lg */}
        <header
          className="lg:hidden sticky top-0 z-30 bg-[#1C352D] text-stone-100 flex items-center justify-between px-3 border-b border-white/10 safe-top"
          data-testid="mobile-topbar"
        >
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
            className="w-10 h-10 grid place-items-center rounded-md hover:bg-white/10 active:bg-white/20 transition-colors"
            aria-label="Apri menu"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 py-2">
            <div className="w-7 h-7 rounded-md bg-[#E2725B] grid place-items-center text-white">
              <Sprout size={14} />
            </div>
            <div className="font-semibold text-sm tracking-tight">RDM ClassRoom</div>
          </div>
          <div className="pr-1">
            <PushEnableButton compact />
          </div>
        </header>

        <main className="flex-1 min-w-0 pb-safe">
          <Outlet />
        </main>
      </div>

      {/* Drawer sidebar mobile */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 sm:max-w-xs bg-[#1C352D] border-0 [&>button]:text-stone-100 [&>button]:hover:bg-white/10 [&>button]:rounded-md"
          data-testid="mobile-sidebar-drawer"
        >
          <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
          <Sidebar mobile onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      <PullToRefresh />
      <Toaster position="top-right" richColors closeButton />
      <ChangePasswordDialog open={mustChange} onOpenChange={() => {}} mustChange />
    </div>
  );
}
